import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Button } from '../../ui/button';
import { BarChart3, TrendingDown } from 'lucide-react';
import { formatCurrency } from './financingCalculations';
import { currencySymbol } from '../../../utils/formatters';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-3 shadow-succession">
      {label && <p className="mb-2 text-sm font-medium text-succession-text-primary">{label}</p>}
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-succession-text-secondary">{entry.name}:</span>
            <span className="font-medium text-succession-text-primary">{formatCurrency(entry.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AmortizationChart({ schedule, className = '' }) {
  const [chartView, setChartView] = useState('stacked');

  const stackedData = schedule.map((year, index) => {
    const previousYears = schedule.slice(0, index);
    const cumulativePrincipal =
      previousYears.reduce((sum, y) => sum + y.totalPrincipal, 0) + year.totalPrincipal;
    const cumulativeInterest =
      previousYears.reduce((sum, y) => sum + y.totalInterest, 0) + year.totalInterest;

    return {
      year: `Year ${year.year}`,
      Principal: Math.round(cumulativePrincipal),
      Interest: Math.round(cumulativeInterest),
    };
  });

  const balanceData = schedule.map((year) => ({
    year: `Year ${year.year}`,
    Balance: Math.round(year.remainingBalance),
  }));

  return (
    <div className={`bg-succession-bg-primary border border-succession-border rounded-xl p-6 shadow-succession transition-colors duration-300 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-succession-text-primary">
            {chartView === 'stacked' ? 'Loan Repayment Over Time' : 'Remaining Balance'}
          </h3>
          <p className="text-sm text-succession-text-secondary mt-1">
            {chartView === 'stacked'
              ? 'Cumulative principal and interest payments'
              : 'Outstanding loan balance declining over time'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={chartView === 'stacked' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartView('stacked')}
            className="gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Repayment
          </Button>
          <Button
            variant={chartView === 'balance' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartView('balance')}
            className="gap-2"
          >
            <TrendingDown className="w-4 h-4" />
            Balance
          </Button>
        </div>
      </div>

      <div style={{ height: 400 }}>
        {chartView === 'stacked' ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stackedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--succession-border)" />
              <XAxis dataKey="year" tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }} />
              <YAxis
                tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }}
                tickFormatter={(value) => `${currencySymbol()}${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="Principal"
                stackId="1"
                stroke="var(--succession-success)"
                fill="var(--succession-success)"
                fillOpacity={0.8}
              />
              <Area
                type="monotone"
                dataKey="Interest"
                stackId="1"
                stroke="var(--succession-accent)"
                fill="var(--succession-accent)"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={balanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--succession-border)" />
              <XAxis dataKey="year" tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }} />
              <YAxis
                tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }}
                tickFormatter={(value) => `${currencySymbol()}${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="Balance"
                stroke="var(--succession-primary)"
                strokeWidth={3}
                dot={{ fill: 'var(--succession-primary)', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-succession-border">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--succession-success)' }} />
          <span className="text-sm text-succession-text-secondary">Principal</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--succession-accent)' }} />
          <span className="text-sm text-succession-text-secondary">Interest</span>
        </div>
        {chartView === 'balance' && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--succession-primary)' }} />
            <span className="text-sm text-succession-text-secondary">Balance</span>
          </div>
        )}
      </div>
    </div>
  );
}
